export function isPremiumMode(value) {
  return value === 'purchase' || value === 'quote_only';
}

export function isAccordionActivationKey(key) {
  return key === 'Enter' || key === ' ' || key === 'Spacebar';
}

export function buildQuoteUrl(baseUrl, context) {
  const url = new URL(baseUrl, 'https://farmguard.invalid');
  for (const [key, value] of Object.entries(context)) {
    if (value) url.searchParams.set(key, value);
  }
  return url.origin === 'https://farmguard.invalid' ? `${url.pathname}${url.search}` : url.toString();
}

export function buildWhatsAppUrl(baseUrl, context) {
  const url = new URL(baseUrl);
  const market = String(context.market || 'AU').toUpperCase();
  const deliveryLabel = market === 'US' ? 'ZIP/State' : 'postcode';
  const lines = [
    'Hello FARM GUARD, I would like a wholesale quotation.',
    `Market: ${market}`,
    context.product && `Product: ${context.product}`,
    context.sku && `SKU if applicable: ${context.sku}`,
    context.variant && `Specification: ${context.variant}`,
    'Quantity:',
    `Delivery ${deliveryLabel}:`,
    context.sourcePage && `Source Page: ${context.sourcePage}`,
    context.page && `Page URL: ${context.page}`,
  ].filter(Boolean);
  url.searchParams.set('text', lines.join('\n'));
  return url.toString();
}

export function normaliseQuantity(value, minimum = 1) {
  const parsed = Math.floor(Number(value));
  return Number.isFinite(parsed) ? Math.max(minimum, parsed) : minimum;
}

function selectedContext(root) {
  const option = root.querySelector('[data-fg-variant-select]')?.selectedOptions?.[0];
  return {
    market: root.dataset.fgMarket || 'au',
    product: root.dataset.fgProductTitle || '',
    product_family: root.dataset.fgProductFamily || '',
    sku: option?.dataset.sku || root.dataset.fgSku || '',
    page: window.location.href.split('#')[0],
    sourcePage: root.dataset.fgSourcePage || '',
    variant: option?.dataset.variantTitle || root.dataset.fgVariantTitle || '',
  };
}

function syncContactLinks(root) {
  const context = selectedContext(root);
  root.querySelectorAll('[data-fg-quote]').forEach((link) => {
    link.href = buildQuoteUrl(root.dataset.fgQuoteBase || '/pages/contact-us', context);
  });
  root.querySelectorAll('[data-fg-whatsapp]').forEach((link) => {
    link.href = buildWhatsAppUrl(root.dataset.fgWhatsappBase || 'https://wa.me/8615951241570', context);
  });
}

function initialiseVariantControls(root) {
  const select = root.querySelector('[data-fg-variant-select]');
  if (!select) {
    syncContactLinks(root);
    return;
  }
  const variantInput = root.querySelector('[data-fg-variant-input]');
  const price = root.querySelector('[data-fg-price]');
  const sku = root.querySelector('[data-fg-sku]');
  const availability = root.querySelector('[data-fg-availability]');
  const addButton = root.querySelector('[data-fg-add]');

  const applyVariant = () => {
    const option = select.selectedOptions[0];
    const available = option?.dataset.available === 'true';
    if (variantInput) variantInput.value = option?.value || '';
    if (price) price.textContent = option?.dataset.price || '';
    if (sku) sku.textContent = option?.dataset.sku || '';
    if (availability) {
      availability.textContent = available
        ? 'Available from China production allocation · Handling time applies'
        : 'Unavailable for direct checkout · Request a written quotation';
    }
    if (addButton) {
      addButton.disabled = !available;
      addButton.setAttribute('aria-disabled', String(!available));
      const label = addButton.querySelector('[data-fg-add-label]');
      if (label) label.textContent = available ? 'Add to cart' : 'Unavailable';
    }
    syncContactLinks(root);
  };
  select.addEventListener('change', applyVariant);
  applyVariant();
}

function initialiseQuantity(root) {
  const input = root.querySelector('[data-fg-quantity]');
  if (!input) return;
  root.querySelectorAll('[data-fg-quantity-change]').forEach((button) => {
    button.addEventListener('click', () => {
      const minimum = Number(input.min || 1);
      const step = Number(input.step || 1);
      const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
      const direction = Number(button.dataset.fgQuantityChange || 0);
      input.value = String(Math.min(maximum, normaliseQuantity(Number(input.value || minimum) + direction * step, minimum)));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

function initialiseGallery(root) {
  const mainImage = root.querySelector('.fg-premium-gallery__main');
  if (!mainImage) return;
  root.querySelectorAll('[data-fg-gallery-thumb]').forEach((button) => {
    button.addEventListener('click', () => {
      mainImage.src = button.dataset.imageSrc;
      mainImage.srcset = '';
      mainImage.alt = button.dataset.imageAlt || '';
      root.querySelectorAll('[data-fg-gallery-thumb]').forEach((item) => {
        item.setAttribute('aria-current', String(item === button));
      });
    });
  });
}

function initialiseAccordions(root) {
  root.querySelectorAll('details[data-fg-accordion]').forEach((details) => {
    const summary = details.querySelector('summary');
    if (!summary) return;
    const sync = () => summary.setAttribute('aria-expanded', String(details.open));
    summary.addEventListener('keydown', (event) => {
      if (!isAccordionActivationKey(event.key)) return;
      event.preventDefault();
      details.open = !details.open;
      sync();
    });
    details.addEventListener('toggle', sync);
    sync();
  });
}

function initialisePremium(root) {
  if (root.dataset.fgReady === 'true') return;
  root.dataset.fgReady = 'true';
  initialiseVariantControls(root);
  initialiseQuantity(root);
  initialiseGallery(root);
  initialiseAccordions(root);
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-fg-premium-root]').forEach(initialisePremium);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-fg-premium-root]').forEach(initialisePremium);
  });
}
